import { ApolloLink, concat, InMemoryCache } from '@apollo/client/core'
import { getGqlURL, getLocale } from 'src/common/functions'
import { createUploadLink } from 'apollo-upload-client'
import * as Sentry from '@sentry/vue'
import { Platform } from 'quasar'
import { onError } from '@apollo/client/link/error'
import { ALLOW_MIRROR } from 'src/common/config'

export function getClientOptions({ store }) {
  const config = {}

  const uri = getGqlURL()

  const httpLink = createUploadLink({
    uri: ({ operationName }) => `${uri}?${operationName}`,
  })

  const authMiddleware = new ApolloLink((operation, forward) => {
    if (Sentry) {
      try {
        let params = ''
        if (Object.keys(operation.variables).length) {
          params = '| ' + JSON.stringify(operation.variables)
        }
        const payload = {
          category: 'gql',
          message: `GQL: ${operation.operationName}${params}`,
          level: Sentry.Severity.Info,
        }
        Sentry.addBreadcrumb(payload)
      } catch (e) {
        console.error(e)
      }
    }

    const JWT = store.getters.getToken ? `JWT ${store.getters.getToken}` : null
    const locale = store.getters.getUserLanguage || getLocale()

    const headers = {
      'client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      accept: 'application/json',
      'Accept-Language': locale,
    }

    if (JWT && operation.operationName !== 'refreshToken') {
      headers['Authorization'] = JWT
    }

    const allowMirror = ALLOW_MIRROR.storage.getItem(ALLOW_MIRROR.query)
    if (JWT && allowMirror && operation.operationName === 'siteInfo') {
      headers[ALLOW_MIRROR.header] = 1
    }

    if (Platform.is.mobile) {
      headers['CASISMOBILE'] = 1
    }

    operation.setContext({
      headers,
    })
    return forward(operation)
  })

  const errorLink = onError(({ graphQLErrors, operation }) => {
    if (graphQLErrors) {
      const variables = Object.entries(operation.variables)
        .map(([key, value]) => {
          return `${key}:${value}`
        })
        .join(';')

      const description = `(${operation.operationName}${variables ? ' | ' + variables : ''})`

      const errors = graphQLErrors.map(({ message }) => {
        return message
      })
      console.error(`GQL errors: ${description}: ${errors.join(' | ')}`)
    }
  })

  config.cache = new InMemoryCache({
    dataIdFromObject: (data) => {
      switch (data.__typename) {
        case 'SiteInfoType': {
          return 'SiteInfoTypeID'
        }
        default: {
          return data?.id ?? null
        }
      }
    },
  })

  config.link = ApolloLink.from([errorLink, concat(authMiddleware, httpLink)])

  return config
}
